/**
 * search/api.ts — the global search field's one backend call (`DS-F20`, `DS-F21`).
 *
 * Lives outside `tools/` because the field is no longer tools-only: it searches the catalogue AND
 * people, and a people call reaching through a module named `tools` would be the first sign of the two
 * search experiences drifting apart, which `DS-F19` exists to prevent.
 */
import { apiGet, apiPost, type ApiResultNoFeature } from '../tools/api';
import {
  isSearchable,
  MAX_SUGGESTIONS,
  MAX_PEOPLE_SUGGESTIONS,
  AVATAR_DATA_URI_RE,
  type PersonSuggestion,
  type SuggestResponse,
  type ToolSuggestion,
} from '@trailblazer/shared';

/** Both groups empty — the answer for "nothing to search" and for every failure. */
const EMPTY: SuggestResponse = { tools: [], people: [] };

/**
 * Keep only what may safely reach an `<img src>`.
 *
 * The server already applies this rule, so this is defence in depth at the render boundary — the same
 * reasoning `Profile.tsx` documents for its own avatar path. A response is data from the network: if it
 * ever carried `data:text/html,<script>…` or a script-bearing SVG, this is the last place to stop it.
 */
function safeAvatar(avatar: string | null): string | null {
  if (typeof avatar !== 'string' || avatar.length === 0) return null;
  return AVATAR_DATA_URI_RE.test(avatar) ? avatar : null;
}

/** Coerce one person from an untrusted response into the shape the dropdown renders. */
function normalisePerson(raw: PersonSuggestion): PersonSuggestion {
  return {
    userId: String(raw.userId),
    name: String(raw.name ?? ''),
    jobTitle: raw.jobTitle ?? null,
    department: raw.department ?? null,
    // Only the two documented markers, whatever the response claims (PR-F24).
    markers: Array.isArray(raw.markers)
      ? raw.markers.filter((m) => m === 'Champion' || m === 'SWAT')
      : [],
    avatar: safeAvatar(raw.avatar ?? null),
  };
}

/**
 * Fetch type-ahead suggestions: up to five tools and up to five people.
 *
 * A POST, not a GET, because `AE-F36` / `DS-F19` forbid recording what someone searched for and this
 * fires as the person types — a query string would put every prefix of every search into access logs.
 *
 * NEVER throws and never rejects. `DS-NFR08` requires that the field "does not slow a page down" and
 * that typing "must never block the page behind it", so every failure — offline, 500, signed out, a
 * mis-shaped body — is an empty dropdown. The person can still submit the query and get the full
 * results view (`DS-F18`).
 */
export async function fetchSuggestions(q: string): Promise<SuggestResponse> {
  if (!isSearchable(q)) return EMPTY;
  try {
    const res: ApiResultNoFeature<SuggestResponse> = await apiPost<SuggestResponse>(
      '/api/search/suggest',
      { q },
    );
    if (res.kind !== 'ok' || !res.body) return EMPTY;
    const tools: ToolSuggestion[] = Array.isArray(res.body.tools)
      ? res.body.tools.slice(0, MAX_SUGGESTIONS)
      : [];
    const people: PersonSuggestion[] = Array.isArray(res.body.people)
      ? res.body.people.slice(0, MAX_PEOPLE_SUGGESTIONS).map(normalisePerson)
      : [];
    return { tools, people };
  } catch {
    return EMPTY;
  }
}

/** The public profile shape, mirroring api/src/db/publicProfile.ts. */
export interface PublicPersonCertification {
  name: string;
  year: number;
}
export interface PublicPersonSelfDeclared {
  bio: string | null;
  interests: string[];
  skillsHave: string[];
  skillsWanted: string[];
  competencyLevel: string | null;
  makerType: string | null;
  certifications: PublicPersonCertification[];
}
/** One chosen displayed badge (PR-F43), mirroring backend PublicBadge. Labels only — never a count. */
export interface PublicPersonBadge {
  badgeKey: string;
  name: string;
  criterionText: string;
}
export interface PublicPerson {
  userId: string;
  name: string;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  team: string | null;
  roleLabel: string | null;
  markers: ('Champion' | 'SWAT')[];
  avatarRef: string | null;
  avatarKind: 'upload' | 'stock' | null;
  photoRef: string | null;
  selfDeclared: PublicPersonSelfDeclared | null;
  /** Chosen displayed badges — `[]` when the person displays none OR opted out (never a count). */
  badges: PublicPersonBadge[];
}

/**
 * One person's public profile (`GET /api/people/:userId`), for the page a suggestion opens.
 *
 * Returns null for a missing OR departed person: the backend conflates those two cases on purpose (so
 * ids cannot be probed to enumerate former colleagues), and the page shows the same plain
 * "not available" either way.
 *
 * A GET here, unlike the suggest call, because a person's id is not search text — nothing about what
 * anyone typed appears in this URL.
 */
export async function fetchPerson(
  userId: string,
): Promise<{ kind: 'ok'; person: PublicPerson } | { kind: 'notFound' } | { kind: 'signedOut' }> {
  const res = await apiGet<PublicPerson>(`/api/people/${encodeURIComponent(userId)}`);
  if (res.kind === 'unauthorized') return { kind: 'signedOut' };
  if (res.kind !== 'ok' || !res.body) return { kind: 'notFound' };
  return { kind: 'ok', person: res.body };
}
